<template>
  <v-list-item
    :class="['session-list-item', { 'session-list-item--active': isActive }]"
    @click="$emit('select', session)"
  >
    <div>
      <v-list-item-title class="session-list-item__title">
        {{ session.title || 'Untitled Conversation' }}
      </v-list-item-title>

      <v-list-item-subtitle class="session-list-item__preview">
        {{ session.summary || '' }}
      </v-list-item-subtitle>

      <div class="session-list-item__meta">
        <span class="session-list-item__meta-item">
          <v-icon size="x-small">mdi-clock-outline</v-icon>
          <span>{{ formattedTimestamp }}</span>
        </span>
        <span class="session-list-item__meta-item">
          <v-icon size="x-small">mdi-message-outline</v-icon>
          <span>{{ session.message_count }} messages</span>
        </span>
      </div>
    </div>

    <template #append>
      <v-btn
        icon
        size="small"
        class="session-list-item__delete"
        aria-label="Delete session"
        @click.stop="$emit('delete', session)"
      >
        <v-icon size="small" color="error">mdi-trash-can-outline</v-icon>
      </v-btn>
    </template>
  </v-list-item>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

import { IChatSession } from '@/bundles/AiAgent/types';
import { formatRelativeTime } from '@/bundles/AiAgent/utils/formatRelativeTime';

export default defineComponent({
  name: 'AiSessionListItem',

  props: {
    session: {
      type: Object as () => IChatSession,
      required: true
    },
    isActive: Boolean
  },

  emits: ['delete', 'select'],

  computed: {
    formattedTimestamp (): string {
      const timestamp = this.session.last_message_at || this.session.created_at;
      return formatRelativeTime(timestamp ? new Date(timestamp) : null);
    }
  }
});
</script>

<style lang="scss" scoped>
.session-list-item {
  border-left: 4px solid transparent;
  transition: background-color 0.2s ease;
  cursor: pointer;

  &:hover {
    background-color: rgba(var(--v-theme-on-surface), var(--v-hover-opacity));

    .session-list-item__delete {
      opacity: 1;
    }
  }

  &--active {
    background-color: rgba(var(--v-theme-primary), 0.08);
    border-left-color: var(--v-primary-base);

    .session-list-item__title {
      color: var(--v-primary-base);
    }
  }

  &__title {
    font-size: 14px;
    font-weight: 500;
    color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  }

  &__preview {
    font-size: 12px;
    color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
    margin-top: 4px;
  }

  &__meta {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 8px;
  }

  &__meta-item {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: rgba(var(--v-theme-on-surface), var(--v-disabled-opacity));

    .v-icon {
      color: rgba(var(--v-theme-on-surface), var(--v-disabled-opacity));
    }
  }

  &__delete {
    opacity: 0;
    transition: opacity 0.2s ease;

    &:hover {
      background-color: rgba(var(--v-theme-error), 0.08);
    }
  }
}
</style>
